import { useEffect, useRef, useState } from 'react'
import { Modal, Badge } from './ui'
import type { Task, TaskLog } from '../lib/api'

interface Props {
  taskId: string
  title?: string
  onClose: () => void
  onComplete?: (t: Task) => void
}

export function TaskProgress({ taskId, title, onClose, onComplete }: Props) {
  const [task, setTask] = useState<Task | null>(null)
  const [logs, setLogs] = useState<TaskLog[]>([])
  const logsEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const token = localStorage.getItem('beebro.token')
    // Fetch API + manual SSE, потому что EventSource не поддерживает custom headers
    const controller = new AbortController()
    ;(async () => {
      try {
        const resp = await fetch(`/api/v1/tasks/${taskId}/events`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        if (!resp.body) return
        const reader = resp.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const parts = buf.split('\n\n')
          buf = parts.pop() || ''
          for (const part of parts) {
            const lines = part.split('\n')
            let event = 'message', data = ''
            for (const l of lines) {
              if (l.startsWith('event: ')) event = l.slice(7).trim()
              else if (l.startsWith('data: ')) data += l.slice(6)
            }
            if (!data) continue
            try {
              const obj = JSON.parse(data)
              if (event === 'state') {
                setTask(obj)
                if ((obj.status === 'ok' || obj.status === 'failed') && onComplete) onComplete(obj)
              } else if (event === 'log') {
                setLogs((prev) => prev.some((x) => x.id === obj.id) ? prev : [...prev, obj])
              }
            } catch {}
          }
        }
      } catch (e) {
        if ((e as any).name !== 'AbortError') console.warn('sse err', e)
      }
    })()
    return () => controller.abort()
  }, [taskId, onComplete])

  useEffect(() => { logsEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [logs])

  return (
    <Modal open onClose={onClose} title={title || `Task ${taskId.slice(0, 8)}`} size="lg">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-neutral-500">Kind:</div>
          <div className="font-mono text-sm">{task?.kind || '…'}</div>
          <div className="ml-auto">{task && <Badge tone={task.status === 'ok' ? 'green' : task.status === 'failed' ? 'red' : 'blue'}>{task.status}</Badge>}</div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-neutral-500 mb-1">
            <span>Progress</span>
            <span>{task?.percent ?? 0}%</span>
          </div>
          <div className="h-2 bg-neutral-200 rounded overflow-hidden">
            <div className="h-full bg-neutral-900 transition-all" style={{ width: `${task?.percent ?? 0}%` }} />
          </div>
        </div>
        {task?.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            <div className="font-medium mb-1">Error</div>
            <div className="font-mono text-xs whitespace-pre-wrap">{task.error}</div>
          </div>
        )}
        <div>
          <div className="text-xs text-neutral-500 mb-1">Logs</div>
          <div className="bg-neutral-900 text-neutral-100 rounded p-3 h-72 overflow-y-auto font-mono text-[11px] leading-relaxed">
            {logs.length === 0 && <div className="text-neutral-500">Waiting for output…</div>}
            {logs.map((l) => (
              <div key={l.id} className={l.stream === 'stderr' || l.stream === 'error' ? 'text-red-300' : ''}>
                <span className="text-neutral-500">{new Date(l.ts).toLocaleTimeString()}</span> {l.line}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>
    </Modal>
  )
}
